<template>
  <div class="task-panel">
    <div class="task-panel__header">
      <div class="task-panel__header-text">
        <h6 class="task-panel__title">Next Task And Follow-Up</h6>
        <p class="task-panel__subtitle">Schedule your next outreach action</p>
      </div>
      <button type="button" class="task-panel__close-btn" @click="closeModal()" aria-label="Close">
        <Icon icon="mdi:close" width="18" height="18" />
      </button>
    </div>
    <Form class="form" @submit="handleSubmit">
      <div class="task-panel__body">
        <div class="card">
          <div class="card-body">
            <div class="row info-row justify-content-between">
              <div class="col-4">
                <h6 class="title">Name</h6>
              </div>
              <div class="col-8">
                <h6 class="value">
                  <a href="#" class="link" @click.prevent="goToContactOverview"
                    >{{ $filters?.capitalize?.(contact?.fname) || contact?.fname }}
                    {{ $filters?.capitalize?.(contact?.lname) || contact?.lname }}</a
                  >
                  <Icon
                    icon="mdi:content-copy"
                    class="action-icon-btn"
                    width="16"
                    height="16"
                    @click="copyToClipboard(`${contact?.fname || ''} ${contact?.lname || ''}`)"
                  />
                </h6>
              </div>
            </div>
            <div class="row info-row justify-content-between">
              <div class="col-4">
                <h6 class="title">Title</h6>
              </div>
              <div class="col-8">
                <h6 v-if="contact?.jobTitle" class="value">
                  {{ contact?.jobTitle }}
                  <Icon
                    icon="mdi:content-copy"
                    class="action-icon-btn"
                    width="16"
                    height="16"
                    @click="copyToClipboard(contact?.jobTitle)"
                  />
                </h6>
                <Icon v-else icon="mdi:minus" width="16" height="16" style="color: #9ca3af" />
              </div>
            </div>
            <div class="row info-row justify-content-between">
              <div class="col-4">
                <h6 class="title">
                  Company
                  <button
                    v-if="contact?.company?.linkedInUrl"
                    type="button"
                    class="linkedin-view-btn"
                    @click="$emit('show-company-profile-modal')"
                    title="View company LinkedIn profile"
                  >
                    <Icon icon="mdi:linkedin" class="linkedin-view-icon" />
                    <span class="linkedin-view-text">View</span>
                  </button>
                </h6>
              </div>
              <div class="col-8">
                <h6 v-if="contact?.company" class="value">
                  <a href="#" class="link" @click.prevent="goToCompanyOverview">{{
                    contact?.company?.name
                  }}</a>
                  <Icon
                    icon="mdi:content-copy"
                    class="action-icon-btn"
                    width="16"
                    height="16"
                    @click="copyToClipboard(contact?.company?.name)"
                  />
                </h6>
                <Icon v-else icon="mdi:minus" width="16" height="16" style="color: #9ca3af" />
              </div>
            </div>
            <div class="row info-row justify-content-between">
              <div class="col-4">
                <h6 class="title">Website</h6>
              </div>
              <div class="col-8">
                <h6 v-if="contact?.company?.website" class="value">
                  <a
                    class="link"
                    :href="
                      $filters?.normalizeWebsite?.(contact?.company?.website) ||
                      contact?.company?.website
                    "
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ contact?.company?.website }}
                  </a>
                  <Icon
                    icon="mdi:open-in-new"
                    class="action-icon-btn"
                    width="16"
                    height="16"
                    @click.stop="
                      openInNewTab(
                        $filters?.normalizeWebsite?.(contact?.company?.website) ||
                          contact?.company?.website
                      )
                    "
                  />
                  <Icon
                    icon="mdi:content-copy"
                    class="action-icon-btn"
                    width="16"
                    height="16"
                    @click.stop="copyToClipboard(contact?.company?.website)"
                  />
                </h6>
                <Icon v-else icon="mdi:minus" width="16" height="16" style="color: #9ca3af" />
              </div>
            </div>
            <div class="row info-row justify-content-between">
              <div class="col-4">
                <h6 class="title">
                  LinkedIn
                  <button
                    v-if="contact?.linkedInUrl"
                    type="button"
                    class="linkedin-view-btn"
                    @click="$emit('show-linkedin-profile-modal')"
                    title="View LinkedIn profile"
                  >
                    <Icon icon="mdi:linkedin" class="linkedin-view-icon" />
                    <span class="linkedin-view-text">View</span>
                  </button>
                </h6>
              </div>
              <div class="col-8">
                <h6 v-if="contact?.linkedInUrl" class="value">
                  <a
                    class="link"
                    :href="contact?.linkedInUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ contact?.linkedInUrl }}
                  </a>
                  <Icon
                    icon="mdi:open-in-new"
                    class="action-icon-btn"
                    width="16"
                    height="16"
                    @click.stop="openInNewTab(contact?.linkedInUrl)"
                  />
                  <Icon
                    icon="mdi:content-copy"
                    class="action-icon-btn"
                    width="16"
                    height="16"
                    @click.stop="copyToClipboard(contact?.linkedInUrl)"
                  />
                </h6>
                <Icon v-else icon="mdi:minus" width="16" height="16" style="color: #9ca3af" />
              </div>
            </div>
            <div class="row info-row justify-content-between">
              <div class="col-4">
                <h6 class="title">Email</h6>
              </div>
              <div class="col-8 info-value-cell">
                <div
                  v-if="contactEmails && contactEmails.length > 0"
                  ref="emailDropdownRef"
                  class="email-dropdown value"
                >
                  <VTooltip>
                    <Icon
                      :icon="getEmailVerificationIcon(contactEmails[0]?.verificationStatus).icon"
                      width="14"
                      height="14"
                      style="cursor: pointer"
                      :style="{
                        color: getEmailVerificationIcon(contactEmails[0]?.verificationStatus).color,
                      }"
                    />
                    <template #popper>
                      <div class="verification-tooltip">
                        <div class="verification-tooltip__header">
                          <Icon
                            :icon="
                              getEmailVerificationIcon(contactEmails[0]?.verificationStatus).icon
                            "
                            width="16"
                            height="16"
                            :style="{
                              color: getEmailVerificationIcon(contactEmails[0]?.verificationStatus)
                                .color,
                            }"
                          />
                          <span class="verification-tooltip__label">{{
                            getEmailVerificationIcon(contactEmails[0]?.verificationStatus).label
                          }}</span>
                        </div>
                        <div class="verification-tooltip__desc">
                          {{
                            getEmailVerificationIcon(contactEmails[0]?.verificationStatus)
                              .description
                          }}
                        </div>
                      </div>
                    </template>
                  </VTooltip>
                  <button
                    v-if="contactEmails.length > 1"
                    class="btn email-dropdown-toggle"
                    type="button"
                    @click="toggleEmailDropdown"
                  >
                    {{ contactEmails[0]?.email }}
                    <Icon icon="mdi:chevron-down" class="email-chevron" width="14" height="14" />
                  </button>
                  <span v-else class="email-display-single">
                    {{ contactEmails[0]?.email }}
                  </span>
                  <Icon
                    icon="mdi:content-copy"
                    class="email-copy-icon"
                    @click="copyToClipboard(contactEmails[0]?.email)"
                    title="Copy email"
                  />
                  <div
                    v-if="contactEmails.length > 1 && emailDropdownOpen"
                    class="email-dropdown-menu"
                  >
                    <button
                      v-for="(emailObj, index) in contactEmails"
                      :key="index"
                      type="button"
                      class="email-dropdown-item"
                      @click="copyToClipboard(emailObj?.email)"
                    >
                      <VTooltip>
                        <Icon
                          :icon="getEmailVerificationIcon(emailObj?.verificationStatus).icon"
                          width="14"
                          height="14"
                          style="cursor: pointer; flex-shrink: 0"
                          :style="{
                            color: getEmailVerificationIcon(emailObj?.verificationStatus).color,
                          }"
                        />
                        <template #popper>
                          <div class="verification-tooltip">
                            <div class="verification-tooltip__header">
                              <Icon
                                :icon="getEmailVerificationIcon(emailObj?.verificationStatus).icon"
                                width="16"
                                height="16"
                                :style="{
                                  color: getEmailVerificationIcon(emailObj?.verificationStatus)
                                    .color,
                                }"
                              />
                              <span class="verification-tooltip__label">{{
                                getEmailVerificationIcon(emailObj?.verificationStatus).label
                              }}</span>
                            </div>
                            <div class="verification-tooltip__desc">
                              {{
                                getEmailVerificationIcon(emailObj?.verificationStatus).description
                              }}
                            </div>
                          </div>
                        </template>
                      </VTooltip>
                      <span>{{ emailObj?.email }}</span>
                      <Icon icon="mdi:content-copy" class="email-dropdown-item-icon" />
                    </button>
                  </div>
                </div>
                <span v-else class="info-value-empty">—</span>
              </div>
            </div>
            <div class="row info-row justify-content-between">
              <div class="col-4">
                <h6 class="title">Phone Numbers</h6>
              </div>
              <div class="col-8 info-value-cell">
                <div
                  v-if="contactPhoneNumbers && contactPhoneNumbers.length > 0"
                  ref="phoneDropdownRef"
                  class="phone-dropdown value"
                >
                  <button
                    v-if="contactPhoneNumbers.length > 1"
                    class="btn phone-dropdown-toggle"
                    type="button"
                    @click="togglePhoneDropdown"
                  >
                    {{
                      $filters?.formatPhoneNumber?.(contactPhoneNumbers[0]?.number) ||
                      contactPhoneNumbers[0]?.number
                    }}
                    <Icon icon="mdi:chevron-down" class="phone-chevron" width="14" height="14" />
                  </button>
                  <span v-else class="phone-display-single">
                    {{
                      $filters?.formatPhoneNumber?.(contactPhoneNumbers[0]?.number) ||
                      contactPhoneNumbers[0]?.number
                    }}
                  </span>
                  <Icon
                    icon="mdi:content-copy"
                    class="phone-copy-icon"
                    @click="copyToClipboard(contactPhoneNumbers[0]?.number)"
                    title="Copy number"
                  />
                  <div
                    v-if="contactPhoneNumbers.length > 1 && phoneDropdownOpen"
                    class="phone-dropdown-menu"
                  >
                    <button
                      v-for="(phoneNumber, index) in contactPhoneNumbers"
                      :key="index"
                      type="button"
                      class="phone-dropdown-item"
                      @click="copyToClipboard(phoneNumber?.number)"
                    >
                      <span>{{
                        $filters?.formatPhoneNumber?.(phoneNumber?.number) || phoneNumber?.number
                      }}</span>
                      <Icon icon="mdi:content-copy" class="phone-dropdown-item-icon" />
                    </button>
                  </div>
                </div>
                <span v-else class="info-value-empty">—</span>
              </div>
            </div>
            <div class="row info-row justify-content-between">
              <div class="col-4">
                <h6 class="title">Timezone</h6>
              </div>
              <div class="col-8 info-value-cell">
                <h6 v-if="contact?.timezone" class="value">
                  <Icon
                    icon="mdi:clock-outline"
                    width="14"
                    height="14"
                    style="color: #6b7280; flex-shrink: 0"
                  />
                  {{ formatTimezone(contact?.timezone) }}
                  <span style="color: #6b7280; font-size: 11px; white-space: nowrap">{{
                    getCurrentTimeInTimezone(contact?.timezone)
                  }}</span>
                  <Icon
                    icon="mdi:content-copy"
                    class="action-icon-btn"
                    width="16"
                    height="16"
                    @click="
                      copyToClipboard(
                        contact?.timezone
                          ? `${formatTimezone(contact.timezone)} — ${getCurrentTimeInTimezone(contact.timezone)}`
                          : ''
                      )
                    "
                  />
                </h6>
                <span v-else class="info-value-empty">—</span>
              </div>
            </div>
          </div>
        </div>
        <div class="action-bar">
          <Button
            type="button"
            variant="outline"
            size="xs"
            class="btn-outline-primary"
            @click="$emit('show-last-interaction-modal')"
          >
            Last Interaction History
          </Button>
          <Button
            type="button"
            variant="primary"
            size="xs"
            class="btn-edit-rounded"
            @click="$emit('show-edit-contact-modal')"
          >
            Edit
          </Button>
        </div>

        <div class="row form-row-tight">
          <div class="col">
            <div class="form-group">
              <Select
                v-model="editBucketObj.bucketOptionId"
                :options="bucketOptionsFormatted"
                label="Bucket"
                :required="true"
              />
            </div>
          </div>
          <div class="col">
            <div class="form-group">
              <Select
                v-model="editBucketObj.taskType"
                :options="taskTypeOptions"
                label="Task Type"
                :required="true"
              />
            </div>
          </div>
        </div>
        <div class="row form-row-tight">
          <div class="col">
            <div class="form-group">
              <Select
                v-model="selectedFollowUpOption"
                :options="followUpOnOptions"
                label="Follow up on"
                placeholder="Select follow up option"
                @change="updateFollowUpDate"
              />
            </div>
          </div>
          <div class="col">
            <div class="form-group">
              <DateSelector
                v-model="editBucketObj.followUpDate"
                label="Follow up date"
                :disable-past="false"
                :required="true"
              />
            </div>
          </div>
        </div>
        <div class="form-group">
          <div class="d-flex justify-content-between align-items-center next-task-note-row">
            <label for="message" class="next-task-note-label">Add Note</label>
            <Button
              type="button"
              variant="outline"
              size="xs"
              class="btn-outline-primary"
              @click="$emit('show-notes-modal')"
            >
              Notes History
            </Button>
          </div>
          <RichTextEditor
            ref="noteEditor"
            v-model="editBucketObj.note"
            height="150px"
            placeholder="Enter here"
            @upload="handleEditorUpload"
            @uploads-pending="pendingUploads = $event"
          />
          <ErrorMessage name="message" class="form-text text-danger" />
        </div>
      </div>
      <div class="task-panel__footer">
        <div class="task-panel__footer-buttons">
          <Button
            @click="$emit('show-book-meeting-modal')"
            type="button"
            variant="primary"
            :disabled="bucketIsLoading"
            block
          >
            Book
          </Button>
          <Button
            type="submit"
            variant="secondary"
            :loading="bucketIsLoading"
            :disabled="bucketIsLoading || pendingUploads > 0"
            block
          >
            Save
          </Button>
        </div>
      </div>
    </Form>
  </div>
</template>

<script setup lang="ts">
import { Icon } from "@iconify/vue";
import { Clipboard } from "v-clipboard";
import { v4 as uuidv4 } from "uuid";
import { Button, DateSelector, RichTextEditor, Select } from "@/components/ui";
import { ErrorMessage, Form } from "vee-validate";
import { TaskType } from "@/utils/enums";
import { getTaskTypeIcon } from "@/utils/iconMapping";
import { COMPANY_OVERVIEW_ROUTE, CONTACT_OVERVIEW_ROUTE } from "@/router/routes";
import { useContactData } from "@/composables/contacts/useContactData";
import { useBucketOptions } from "@/composables/bucket/useBucketOptions";
import { useBucketLockStatus } from "@/composables/bucket/useBucketLockStatus";
import { useUploadDraftNoteAttachment, useDeleteNoteDraftAttachments } from "@/composables/notes";
import { ref, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance } from "vue";
import { useToast } from "@/composables/ui/useToast";
import { useFormDraft } from "@/composables/shared";
import { useStore } from "vuex";
import { formatTimezone, getCurrentTimeInTimezone } from "@/utils/timezone";
import { getEmailVerificationIcon } from "@/utils/emailVerificationStatus";
import type { ContactBucketSummary } from "@/dtos/Bucket.dto";
import { useRouter } from "vue-router";

defineOptions({
  name: "NextTaskFollowupModal",
});

interface Props {
  selectedBucket: {
    id?: string | null;
    taskType?: string | null;
    followUpDate?: string | null;
    bucketOptionId?: string | null;
    note?: string | null;
    contact?: {
      id?: string;
      fname?: string;
      lname?: string;
      jobTitle?: string;
      email?: string;
      linkedInUrl?: string;
      timezone?: string;
      company?: {
        id?: string;
        name?: string;
        website?: string;
        linkedInUrl?: string;
      };
    };
  };
  contact?: {
    id?: string;
    fname?: string;
    lname?: string;
    jobTitle?: string;
    email?: string;
    linkedInUrl?: string;
    timezone?: string;
    phones?: Array<any>;
    emails?: Array<any>;
    company?: {
      id?: string;
      name?: string;
      website?: string;
      linkedInUrl?: string;
    };
    bucket?: ContactBucketSummary | null;
  } | null;
  bucketIsLoading?: boolean;
  bucketOptions?: Array<{ id: string; name: string }>;
}

const props = withDefaults(defineProps<Props>(), {
  contact: null,
  bucketIsLoading: false,
  bucketOptions: () => [],
});

const emit = defineEmits<{
  (e: "close"): void;
  (e: "save", editBucketObj: any): void;
  (e: "show-notes-modal"): void;
  (e: "show-last-interaction-modal"): void;
  (e: "show-edit-contact-modal"): void;
  (e: "show-book-meeting-modal"): void;
  (e: "show-linkedin-profile-modal"): void;
  (e: "show-company-profile-modal"): void;
}>();

const instance = getCurrentInstance();
const $toast = useToast();
const $filters = instance?.proxy && (instance.proxy as any).$filters;
const router = useRouter();

// Store
const store = useStore();
const account = computed(() => store.getters.account.data);
const organizationId = computed(() => account.value?.organizationId);

// State
const editBucketObj = ref({
  id: undefined as string | undefined,
  taskType: undefined as string | undefined,
  followUpDate: undefined as string | undefined,
  bucketOptionId: undefined as string | undefined,
  note: undefined as string | undefined,
});

const selectedFollowUpOption = ref<string | undefined>(undefined);

// This note isn't a persisted entity until the bucket itself is saved (see handleSubmit),
// so uploads have nowhere to attach to yet — route them through the same client-generated
// draftKey used by NotesModal.vue's unsaved-note flow, which just needs a grouping key.
//
// Unlike NotesModal.vue, there's no backend support for linking a draft's attachments to
// a Bucket/Task note (NoteAttachment only has a noteId, not a bucketId) — so a saved note's
// embedded file link has no server-side record tying it to the note that references it.
// What we CAN still guarantee from here: never leave an upload orphaned when the user backs
// out without saving. hasSubmitted marks "a save was attempted with this draftKey" so we
// never discard an attachment that might already be embedded in a persisted note — we only
// clean up drafts that were never submitted at all.
const draftKey = ref<string | null>(null);
const pendingUploads = ref(0);
let hasSubmitted = false;
const uploadDraft = useUploadDraftNoteAttachment();
const discardDraft = useDeleteNoteDraftAttachments();

const discardAbandonedDraft = () => {
  if (!draftKey.value || hasSubmitted) return;
  discardDraft.mutate(draftKey.value, { onError: () => {} });
  draftKey.value = null;
};

// Shared with NotesModal.vue's "Add Note" form — both are just different entry
// points for jotting an unsaved note about the same contact, so they read/write
// the same draft under a common contact-scoped key.
const { loadDraft: loadNoteTextDraft, clearDraft: clearNoteTextDraft } = useFormDraft(
  editBucketObj,
  {
    key: `contact-note-draft:${props.contact?.id}`,
    serialize: (v) => ({ text: v.note ?? "" }),
    isEmpty: (v) => !v.text?.trim(),
  }
);

const handleEditorUpload = async (payload: {
  file: File;
  kind: "image" | "video" | "file";
  placeholderId?: string;
  signal?: AbortSignal;
  onProgress?: (pct: number) => void;
  onSuccess?: (url: string) => void;
  onError?: (msg?: string) => void;
}) => {
  try {
    if (!draftKey.value) draftKey.value = uuidv4();
    const result = await uploadDraft.mutateAsync({
      draftKey: draftKey.value,
      files: [payload.file],
      onProgress: payload.onProgress,
      signal: payload.signal,
    });
    const uploaded = result?.[0];
    if (uploaded?.url && payload.onSuccess) {
      payload.onSuccess(uploaded.url);
    } else {
      payload.onError?.("Upload failed");
    }
  } catch (err: any) {
    if (err?.code === "ERR_CANCELED" || err?.name === "AbortError") return;
    payload.onError?.(err?.message || "Upload failed");
  }
};

// Bucket options query
const bucketOptionsParams = computed(() => {
  if (!organizationId.value) return null;
  return {
    organizationId: organizationId.value,
  };
});

const { data: bucketOptionsData } = useBucketOptions(bucketOptionsParams);

// Bucket lock status query
const bucketLockParams = computed(() => {
  if (!organizationId.value) return null;
  return {
    organizationId: organizationId.value,
    offset: new Date().getTimezoneOffset(),
  };
});
const { data: bucketLockStatusData } = useBucketLockStatus(bucketLockParams);

// Get set of locked bucket IDs
const lockedBucketIds = computed(() => {
  if (!bucketLockStatusData.value?.enabled) return new Set<string>();
  return new Set(
    bucketLockStatusData.value.bucketStatuses
      .filter((s: any) => s.isLocked && !s.hasOverride)
      .map((s: any) => s.bucketOptionId)
  );
});

// Computed properties
const contactPhoneNumbers = computed(() => {
  const { computeContactPhoneNumbers } = useContactData(store);
  return computeContactPhoneNumbers({ contact: props.contact });
});

const contactEmails = computed(() => {
  const { computeContactEmails } = useContactData(store);
  return computeContactEmails({ contact: props.contact });
});

const taskTypeOptions = computed(() => {
  return Object.values(TaskType).map((task) => ({
    value: task,
    label: $filters?.capitalize?.(task) || task,
    icon: getTaskTypeIcon(task),
  }));
});

const bucketOptionsFormatted = computed(() => {
  const options = bucketOptionsData.value || props.bucketOptions || [];
  return options.map((bucket: any) => {
    const isLocked = lockedBucketIds.value.has(bucket.id);
    return {
      value: bucket.id,
      label: isLocked ? `🔒 ${bucket.name} (Locked)` : bucket.name,
      disabled: isLocked,
      icon: "fa6-solid:bucket",
      iconColor: bucket.colorHex,
    };
  });
});

const followUpOnOptions = [
  { value: "1", label: "Follow Up Tomorrow" },
  { value: "3", label: "Follow Up In 3 Days" },
  { value: "7", label: "Follow Up In 1 Week" },
  { value: "14", label: "Follow Up In 2 Weeks" },
  { value: "30", label: "Follow Up In 1 Month" },
  { value: "60", label: "Follow Up In 2 Months" },
  { value: "90", label: "Follow Up In 3 Months" },
  { value: "180", label: "Follow Up In 6 Months" },
  { value: "365", label: "Follow Up In 1 Year" },
];

// Methods
const closeModal = () => {
  discardAbandonedDraft();
  emit("close");
};

// This modal is mounted globally (see App.vue), so router-link navigation alone
// doesn't unmount it — we navigate first, then explicitly close it once the
// route change has gone through.
const goToContactOverview = async () => {
  const contactId = props.contact?.id;
  if (!contactId) return;
  await router.push({ name: CONTACT_OVERVIEW_ROUTE, params: { id: contactId } });
  closeModal();
};

const goToCompanyOverview = async () => {
  const companyId = props.contact?.company?.id;
  if (!companyId) return;
  await router.push({ name: COMPANY_OVERVIEW_ROUTE, params: { id: companyId } });
  closeModal();
};

const openInNewTab = (url: string | undefined | null) => {
  if (url) window.open(url, "_blank");
};

const emailDropdownOpen = ref(false);
const phoneDropdownOpen = ref(false);
const emailDropdownRef = ref<HTMLElement | null>(null);
const phoneDropdownRef = ref<HTMLElement | null>(null);

const toggleEmailDropdown = () => {
  emailDropdownOpen.value = !emailDropdownOpen.value;
  phoneDropdownOpen.value = false;
};

const togglePhoneDropdown = () => {
  phoneDropdownOpen.value = !phoneDropdownOpen.value;
  emailDropdownOpen.value = false;
};

const handleClickOutside = (e: MouseEvent) => {
  if (emailDropdownRef.value && !emailDropdownRef.value.contains(e.target as Node)) {
    emailDropdownOpen.value = false;
  }
  if (phoneDropdownRef.value && !phoneDropdownRef.value.contains(e.target as Node)) {
    phoneDropdownOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener("mousedown", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", handleClickOutside);
  discardAbandonedDraft();
});

const copyToClipboard = (value: string | undefined | null) => {
  if (!value) return;
  Clipboard.copy(value);
  $toast?.info("Copied to clipboard");
};

const updateFollowUpDate = () => {
  if (!selectedFollowUpOption.value) return;
  const daysToAdd = parseInt(selectedFollowUpOption.value, 10);
  const target = new Date();
  target.setDate(target.getDate() + daysToAdd);
  const localMidnight = new Date(
    target.getFullYear(),
    target.getMonth(),
    target.getDate(),
    0,
    0,
    0,
    0
  );
  editBucketObj.value.followUpDate = localMidnight.toISOString();
};

const handleSubmit = () => {
  hasSubmitted = true;
  clearNoteTextDraft();
  emit("save", editBucketObj.value);
};

// The task itself may not have a bucketOptionId stamped on it (e.g. it predates
// the contact being auto-bucketed), so fall back to the contact's live bucket.
const contactBucketOptionId = computed(
  () => props.contact?.bucket?.bucketOptionId ?? props.contact?.bucket?.bucketOption?.id
);

// Lifecycle
onMounted(() => {
  if (props.selectedBucket) {
    editBucketObj.value.id = props.selectedBucket.id ?? undefined;
    editBucketObj.value.taskType = props.selectedBucket.taskType ?? undefined;
    editBucketObj.value.followUpDate = props.selectedBucket.followUpDate ?? undefined;
    editBucketObj.value.bucketOptionId =
      props.selectedBucket.bucketOptionId ?? contactBucketOptionId.value ?? undefined;
    editBucketObj.value.note = props.selectedBucket.note ?? undefined;
  }
  const draft = loadNoteTextDraft();
  if (draft?.text?.trim()) {
    editBucketObj.value.note = draft.text;
  }
});

// The contact's bucket data loads asynchronously (after this modal mounts), so
// backfill once it arrives if the task didn't already have a bucket selected.
watch(contactBucketOptionId, (newVal) => {
  if (newVal && !editBucketObj.value.bucketOptionId) {
    editBucketObj.value.bucketOptionId = newVal;
  }
});
</script>

<style scoped>
.task-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--color-bg-white, #fff);
}

.task-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(90deg, #2d3bff 0%, #4f63ff 100%);
  color: #fff;
  padding: 18px 20px;
  flex-shrink: 0;
  border-radius: 12px 12px 0 0;
}

.task-panel__header-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.task-panel__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  letter-spacing: -0.01em;
}

.task-panel__subtitle {
  margin: 0;
  font-size: 0.8rem;
  opacity: 0.85;
}

.task-panel__close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #fff;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.task-panel__close-btn:hover {
  background: rgba(255, 255, 255, 0.35);
}

.task-panel > .form {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.task-panel__body {
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 15px;
}

.card {
  padding: 0px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  margin-bottom: 10px;
}

.card-body {
  padding: 8px 10px;
}

.card .info-row {
  margin-bottom: 0;
  padding: 6px 8px;
  border-radius: 6px;
  transition: background-color 0.15s ease;
}

.card .info-row:hover {
  background-color: #f9fafb;
}

.card .title {
  font-weight: 600;
  font-size: 12px;
  color: #6b7280;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  white-space: nowrap;
}

.card .info-value-cell {
  text-align: right;
}

.card .value {
  font-weight: 600;
  font-size: 12px;
  color: #101828;
  float: right;
  display: flex;
  align-items: center;
  gap: 4px;
}

.card .value a,
.card .value .link {
  color: #3e3efe;
  text-decoration: none;
}

.card .value a:hover,
.card .value .link:hover {
  text-decoration: underline;
}

.action-icon-btn {
  color: #3e3efe;
  cursor: pointer;
  margin-left: 4px;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.info-row:hover .action-icon-btn {
  opacity: 1;
}

.card .info-value-empty {
  font-size: 12px;
  color: #6b7280;
}

.btn-outline-primary.ui-button {
  color: #3e3efe;
  border-color: #3e3efe;
  border-radius: 9999px;
  padding: 4px 14px;
  font-size: 11px;
}

.btn-outline-primary.ui-button:hover {
  background-color: #f3f1ff;
  color: #3e3efe;
  border-color: #3e3efe;
}

.btn-outline-primary.ui-button :deep(.ui-button__icon) {
  color: #3e3efe;
}

.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.form-row-tight {
  --bs-gutter-x: 10px;
  flex-wrap: wrap;
  row-gap: 12px;
}

.form-row-tight > .col {
  flex: 1 1 220px;
  min-width: 0;
}

.btn-edit-rounded.ui-button {
  border-radius: 9999px;
  padding: 4px 14px;
  font-size: 11px;
}

.task-panel__footer {
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
  padding: 14px 15px;
}

.task-panel__footer-buttons {
  display: flex;
  gap: 12px;
  width: 100%;
}

.task-panel__footer-buttons .ui-button {
  flex: 1;
  min-width: 0;
}

.next-task-note-row {
  margin-bottom: 6px;
}

.next-task-note-label {
  display: block;
  font-size: 12px;
  font-weight: 400;
  color: #344054;
  margin-bottom: 0;
}

:deep(.date-selector__required) {
  font-size: 12px;
  line-height: 1;
  vertical-align: middle;
}

:deep(.date-selector__label),
:deep(.ui-select__label) {
  height: 18px;
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}

.modal-title-custom {
  font-size: 0.95rem !important;
  font-weight: 400 !important;
}

.modal-close-btn {
  color: #ffffff !important;
}

.modal-close-btn .iconify {
  color: #ffffff !important;
}

.btn-sms {
  font-size: 11px !important;
  padding: 4px 8px !important;
  width: auto !important;
  min-width: 60px;
  background-color: #10b981;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
}

.btn-sms:hover {
  background-color: #059669;
  transform: translateY(-1px);
}

.linkedin-link-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.linkedin-url-row {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 4px;
}

.linkedin-link {
  font-size: 12px;
  font-weight: 500;
  color: #3e3efe;
  text-decoration: none;
  word-break: break-all;
  line-height: 1.4;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.linkedin-action-icon {
  color: #3e3efe;
  cursor: pointer;
  font-size: 16px;
  transition: color 0.2s ease;
  flex-shrink: 0;
}

.linkedin-action-icon:hover {
  color: #2d2dff;
}

.linkedin-view-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  padding: 4px 8px;
  background: #f3f1ff;
  border: 1px solid #3e3efe;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  vertical-align: middle;
  font-size: 11px;
  font-weight: 500;
  color: #3e3efe;
  outline: none;
  box-shadow: none;
  flex-shrink: 0;
  white-space: nowrap;
}

.linkedin-view-btn:hover {
  background: #3e3efe;
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(62, 62, 254, 0.25);
}

.linkedin-view-btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 3px rgba(62, 62, 254, 0.2);
}

.linkedin-view-icon {
  font-size: 14px;
  transition: all 0.2s ease;
}

.linkedin-view-text {
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
}

.phone-dropdown {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  position: relative;
}

.phone-dropdown-toggle {
  padding: 0;
  margin: 0;
  background: none;
  border: none;
  font-size: 12px;
  font-weight: 500;
  color: #3e3efe;
  text-decoration: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.phone-dropdown-toggle:hover,
.phone-dropdown-toggle:focus {
  color: #2d2dff;
  text-decoration: none;
  outline: none;
  box-shadow: none;
}

.phone-chevron {
  color: #3e3efe;
  flex-shrink: 0;
}

.phone-display-single {
  font-size: 12px;
  font-weight: 500;
  color: #3e3efe;
  margin-left: 0;
}

.phone-copy-icon {
  color: #3e3efe;
  cursor: pointer;
  font-size: 16px;
  flex-shrink: 0;
  transition: color 0.2s ease;
}

.phone-copy-icon:hover {
  color: #2d2dff;
}

.phone-dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1050;
  padding: 4px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  background: #fff;
  margin-top: 4px;
}

.phone-dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 500;
  color: #1e1e2f;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;
  white-space: nowrap;
  border-radius: 6px;
}

.phone-dropdown-item:hover {
  background-color: #f5f5ff;
}

.phone-dropdown-item:hover .phone-dropdown-item-icon {
  color: #3e3efe;
}

.phone-dropdown-item-icon {
  color: #9ca3af;
  flex-shrink: 0;
  font-size: 14px;
}

.email-dropdown {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  position: relative;
}

.email-dropdown-toggle {
  padding: 0;
  margin: 0;
  background: none;
  border: none;
  font-size: 12px;
  font-weight: 500;
  color: #3e3efe;
  text-decoration: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.email-dropdown-toggle:hover,
.email-dropdown-toggle:focus {
  color: #2d2dff;
  text-decoration: none;
  outline: none;
  box-shadow: none;
}

.email-chevron {
  color: #3e3efe;
  flex-shrink: 0;
}

.email-display-single {
  font-size: 12px;
  font-weight: 500;
  color: #3e3efe;
  margin-left: 0;
}

.email-copy-icon {
  color: #3e3efe;
  cursor: pointer;
  font-size: 16px;
  flex-shrink: 0;
  transition: color 0.2s ease;
}

.email-copy-icon:hover {
  color: #2d2dff;
}

.email-dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1050;
  padding: 4px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  background: #fff;
  margin-top: 4px;
}

.email-dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 500;
  color: #1e1e2f;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;
  white-space: nowrap;
  border-radius: 6px;
}

.email-dropdown-item:hover {
  background-color: #f5f5ff;
}

.email-dropdown-item:hover .email-dropdown-item-icon {
  color: #3e3efe;
}

.email-dropdown-item-icon {
  color: #9ca3af;
  flex-shrink: 0;
  font-size: 14px;
}
</style>
